import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Star, Play, Film, ShoppingCart, Heart, Share2 } from 'lucide-react';
import { creatorsApi, formatDuration } from '../lib/api';
import { DonationPopup } from './DonationPopup';
import { toast } from 'sonner';

const CreatorProfileModal = ({ creatorName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    creatorsApi.getFilms(creatorName)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [creatorName]);

  const handleContentClick = (filmId) => {
    onClose();
    navigate(`/film/${filmId}`);
  };

  const handleShop = () => {
    if (data?.company_id) {
      onClose();
      navigate(`/shop/creator/${data.company_id}`);
    }
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/browse?creator=${encodeURIComponent(creatorName)}`;
    if (navigator.share) {
      navigator.share({
        title: creatorName,
        text: `Check out ${creatorName} on reframes`,
        url: shareUrl,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Creator link copied!');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose} data-testid="creator-profile-modal">
      <div className="bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-white/5 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                data-testid="creator-modal-name">
                {creatorName}
              </h2>
              {data && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="flex items-center gap-1 text-sm text-[#00FF9D] font-semibold"
                    data-testid="creator-modal-rating">
                    <Star className="w-3.5 h-3.5 fill-[#00FF9D]" />
                    {data.avg_rating?.toFixed(1) || '0.0'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {data.films?.length || 0} title{(data.films?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              data-testid="creator-modal-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Action Buttons */}
          {data && (
            <div className="flex items-center gap-2 mt-4" data-testid="creator-modal-actions">
              {data.company_id && data.stripe_connected && data.has_merch_items && (
                <button
                  onClick={handleShop}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#8B5CF6]/15 text-[#A78BFA] text-xs font-medium hover:bg-[#8B5CF6]/25 transition-colors border border-[#8B5CF6]/20"
                  data-testid="creator-modal-shop-btn"
                >
                  <ShoppingCart className="w-3.5 h-3.5" />
                  Shop
                </button>
              )}
              {data.company_id && data.stripe_connected && (
                <DonationPopup
                  creatorId={data.company_id}
                  creatorName={creatorName}
                  trigger={
                    <button
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pink-500/15 text-pink-400 text-xs font-medium hover:bg-pink-500/25 transition-colors border border-pink-500/20"
                      data-testid="creator-modal-donate-btn"
                    >
                      <Heart className="w-3.5 h-3.5" />
                      Donate
                    </button>
                  }
                />
              )}
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 text-gray-300 text-xs font-medium hover:bg-white/10 transition-colors border border-white/10"
                data-testid="creator-modal-share-btn"
              >
                <Share2 className="w-3.5 h-3.5" />
                Share
              </button>
            </div>
          )}
        </div>

        {/* Content list */}
        <div className="flex-1 overflow-y-auto p-3" data-testid="creator-modal-content-list">
          {loading ? (
            <div className="flex justify-center py-8"><div className="spinner" /></div>
          ) : data?.films?.length > 0 ? (
            <div className="space-y-1">
              {data.films.map(film => (
                <button key={film.film_id}
                  onClick={() => handleContentClick(film.film_id)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.04] transition-colors text-left group"
                  data-testid={`creator-content-${film.film_id}`}>
                  <div className="relative w-24 shrink-0 rounded-md overflow-hidden aspect-video">
                    <img src={film.thumbnail} alt={film.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" loading="lazy" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <Play className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate group-hover:text-[#8B5CF6] transition-colors"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {film.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                      {film.avg_rating > 0 && (
                        <span className="flex items-center gap-0.5 text-[#8B5CF6]">
                          <Star className="w-2.5 h-2.5 fill-current" /> {film.avg_rating?.toFixed(1)}
                        </span>
                      )}
                      {film.year > 0 && <span>{film.year}</span>}
                      {film.duration ? <span>{formatDuration(film.duration)}</span> : null}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">
              <Film className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No content found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorProfileModal;
